import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {FormControl, NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {ContractService} from '../../../shared/services/contract.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../shared/services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {ContractInsuranceCompany} from '../../../shared/interface/contract-insurance-company';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {HttpErrorResponse} from '@angular/common/http';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {CommissionTable, CommissionTableListItem} from "../../../shared/interface/commission-table";
import {CommissionTableService} from "../../../shared/services/commission-table.service";
import {TableViewStorageService} from "../../../shared/services/table-view-storage.service";

@Component({
  selector: 'app-all-contract-insurance-companies-list',
  templateUrl: './all-contract-insurance-companies-list.component.html',
  styleUrls: ['./all-contract-insurance-companies-list.component.scss']
})
export class AllContractInsuranceCompaniesListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() contract?: string = null;
  @Input() statusKeys?: string[] = null;
  @Input() sent?: number = null;
  @Input() returned?: number = null;
  @Input() signed?: number = null;
  @Input() onlyNew?: number = null;
  @Input() disableFilters = false;
  @Input() disableBreadcrumb = false;
  @Input() excludeColumns: string[] = [];
  @Input() reloadList?: ReplaySubject<any>;
  @Input() embeddedView = false;

  initialFilters: any;
  contractInsuranceCompanies: ContractInsuranceCompany[] = [];
  private readonly allColumns: string[] = [
    'logo',
    'insuranceCompany',
    'contract',
    'sent',
    'signed',
    'returned',
    'party',
    'status',
    'assetsRegional',
    'createdAt',
    'sentAt',
    'activeRegistration',
    'standard',
    'note',
    'actions',
  ];

  readonly selectAllColumnsValue = '__all__';

  readonly columnDefinitions: { key: string; label: string }[] = [
    {key: 'logo', label: 'Logo'},
    {key: 'insuranceCompany', label: 'TU'},
    {key: 'contract', label: 'Umowa'},
    {key: 'sent', label: 'Wysłany'},
    {key: 'signed', label: 'Podpisany'},
    {key: 'returned', label: 'Zwrócony'},
    {key: 'party', label: 'Partner/OFWCA'},
    {key: 'status', label: 'Status'},
    {key: 'assetsRegional', label: 'Regionalny (majątek)'},
    {key: 'createdAt', label: 'Data utworzenia'},
    {key: 'sentAt', label: 'Data wysłania'},
    {key: 'activeRegistration', label: 'Aktywna rejestracja pod umową'},
    {key: 'standard', label: 'Standard'},
    {key: 'note', label: 'Uwagi'},
  ];

  displayedColumns: string[] = [...this.allColumns];
  totalData = 0;
  updateInProgress = false;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private readonly tableStorageName = 'contract-insurance-companies';

  commissionTables: CommissionTableListItem[] = [];
  commissionTableControls: { [uuid: string]: FormControl } = {};
  selectedColumnValues: string[] = [];

  constructor(
    private contractService: ContractService,
    private commissionTableService: CommissionTableService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
    private tableViewStorageService: TableViewStorageService
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.contractService.contractInsuranceCompanyList
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<ContractInsuranceCompany>) => {
          this.initializeCommissionTableControls(e.items);
          this.contractInsuranceCompanies = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      contract: 'string',
      in$insuranceCompany: 'int[]',
      sent: 'int',
      signed: 'int',
      returned: 'int',
      assetsRegional: 'string',
      ofwca: 'string',
      partner: 'string',
      status: 'int',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      disabled: 'int',
      newPartner: 'int',
      contractActiveAt: 'string',
      contractActiveTo: 'string',
      partnerCreatedAt: 'string',
      partnerCreatedTo: 'string',
      insuranceCompanyInt: 'int',
    }, filters);
  }

  ngOnInit(): void {
    this.commissionTableService
      .getOptions()
      .pipe(
        first()
      )
      .subscribe(
        (
          commissionTables:
          CommissionTableListItem[]
        ) => {
          this.commissionTables =
            commissionTables.sort(
              (
                a: CommissionTableListItem,
                b: CommissionTableListItem
              ) =>
                (b.validFrom || '')
                  .localeCompare(
                    a.validFrom || ''
                  )
            );
        }
      );
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.contract = this.contract ?? data.contract ?? null;
        this.statusKeys = this.statusKeys ?? data.statusKeys ?? null;
        this.sent = this.sent ?? data.sent ?? null;
        this.signed = this.signed ?? data.signed ?? null;
        this.returned = this.returned ?? data.returned ?? null;
        this.onlyNew = this.onlyNew ?? data.onlyNew ?? null;

        this.displayedColumns = this.displayedColumns.filter((e: string) => {
          return this.excludeColumns.indexOf(e) === -1;
        });

        this.initializeColumnSelection();

        if (!this.embeddedView) {
          this.store
            .pipe(
              select(getCurrentRouteState),
              delay(0),
              distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
              takeUntil(this.destroyed$),
            )
            .subscribe((state: RouterStateUrl) => {
              this.parseFilters(state.params);
              this.paginator.pageIndex = state.params.page;
              this.paginator.pageSize = state.params.pageSize;
              this.loadListData();
            })
          ;
        } else {
          this.loadListData();
        }
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      if (this.embeddedView) {
        this.loadListData();
      } else {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          ...this.filtersForm.value
        })]);
      }
    });

    if (this.reloadList) {
      this.reloadList
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.loadListData());
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.contract) {
      filters.contract = this.contract;
    }
    if (null !== this.statusKeys) {
      filters.statusKeys = this.statusKeys;
    }
    if (null !== this.sent) {
      filters.sent = this.sent;
    }
    if (null !== this.signed) {
      filters.signed = this.signed;
    }
    if (null !== this.returned) {
      filters.returned = this.returned;
    }
    if (null !== this.onlyNew) {
      filters.onlyNew = this.onlyNew;
    }
    filters.insuranceCompanyInt = filters.in$insuranceCompany.length;
    return filters;
  }

  loadListData(): void {
    this.contractService.loadInsuranceCompanyList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  filter(): void {
    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value,
      })]);
    }
  }

  downloadReport(): void {
    this.contractService.downloadInsuranceCompaniesReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  downloadAdditionalReport(): void {
    this.contractService.downloadInsuranceCompaniesAdditionalReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
      }]);
    }
  }

  formChanged(
    $event: MatSlideToggleChange,
    contractInsuranceCompany: ContractInsuranceCompany,
    formData: {returned: boolean; sent: boolean; note: string; signed: boolean}
  ): void {
    this.updateInProgress = true;

    this.contractService.editContractInsuranceCompany(contractInsuranceCompany.uuid, formData)
      .subscribe(() => {
        this.snack.open('Pomyślnie zakutalizowano załącznik', '', {duration: 2000});
        this.updateInProgress = false;
        contractInsuranceCompany.note = formData.note;
        contractInsuranceCompany.sent = formData.sent;
        contractInsuranceCompany.signed = formData.sent && formData.signed;
        contractInsuranceCompany.returned = formData.sent && formData.returned;
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy aktualizacji wystąpił błąd', error.status.toString(), {duration: 2000});
        this.updateInProgress = false;
      });
  }

  deleteInsuranceCompany(contractInsuranceCompany: ContractInsuranceCompany): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć załącznik?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.contractService.deleteContractInsuranceCompany(contractInsuranceCompany.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto załącznik', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }

  noteChanged($event: any, contractInsuranceCompany: ContractInsuranceCompany): void {
    this.updateInProgress = true;
    const form = {
      returned: contractInsuranceCompany.returned,
      sent: contractInsuranceCompany.sent,
      signed: contractInsuranceCompany.signed,
      note: $event.target.value,
    };

    this.contractService.editContractInsuranceCompany(contractInsuranceCompany.uuid, form)
      .subscribe(() => {
        this.snack.open('Pomyślnie zakutalizowano załącznik', '', {duration: 2000});
        this.updateInProgress = false;
        contractInsuranceCompany.note = $event.target.value;
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy aktualizacji wystąpił błąd', error.status.toString(), {duration: 2000});
        this.updateInProgress = false;
      });
  }

  private initializeCommissionTableControls(
    items: ContractInsuranceCompany[]
  ): void {
    const controls: { [uuid: string]: FormControl } = {};

    items.forEach((item: ContractInsuranceCompany) => {
        controls[item.uuid] = new FormControl(item.commissionTable?.uuid ?? null);
      }
    );

    this.commissionTableControls =
      controls;
  }

  commissionTableChanged(
    contractInsuranceCompany:
    ContractInsuranceCompany,
    commissionTable: CommissionTableListItem
  ): void {

    const previousCommissionTable = contractInsuranceCompany.commissionTable;
    if (previousCommissionTable?.uuid === commissionTable.uuid) {
      return;
    }

    this.updateInProgress = true;

    const form = {
      returned:contractInsuranceCompany.returned,
      sent: contractInsuranceCompany.sent,
      signed: contractInsuranceCompany.signed,
      note: contractInsuranceCompany.note,
      commissionTable: commissionTable.uuid,
    };

    this.contractService
      .editContractInsuranceCompany(contractInsuranceCompany.uuid, form)
      .subscribe(
        () => {
          contractInsuranceCompany.commissionTable = commissionTable;
          this.updateInProgress = false;
          this.snack.open(
            'Pomyślnie zaktualizowano standard prowizyjny',
            '',
            {
              duration: 2000
            }
          );
        },

        (error: HttpErrorResponse) => {
          this.commissionTableControls[contractInsuranceCompany.uuid].setValue(previousCommissionTable?.uuid ?? null,
            {
              emitEvent: false
            }
          );

          this.updateInProgress = false;
          this.snack.open('Przy aktualizacji standardu prowizyjnego wystąpił błąd', error.status.toString(),
            {
              duration: 3000
            }
          );
        }
      );
  }

  get availableColumnDefinitions(): { key: string; label: string }[] {
    return this.columnDefinitions.filter(
      (column: { key: string; label: string }) => this.excludeColumns.indexOf(column.key) === -1
    );
  }

  get columnsSelectionLabel(): string {
    const selectedCount = this.selectedColumnValues.filter(
      (column: string) => column !== this.selectAllColumnsValue
    ).length;
    const availableCount = this.availableColumnDefinitions.length;

    if (selectedCount === availableCount) {
      return 'Wszystkie';
    }

    if (selectedCount === 0) {
      return 'Brak kolumn';
    }

    return `${selectedCount} z ${availableCount}`;
  }


  columnSelectionChanged(selectedValues: string[]): void {
    const availableColumns = this.availableColumnDefinitions.map(
      (column: { key: string; label: string }) => column.key
    );
    const selectAllWasSelected = this.selectedColumnValues.indexOf(this.selectAllColumnsValue) !== -1;
    const selectAllIsSelected = selectedValues.indexOf(this.selectAllColumnsValue) !== -1;

    let nextSelectedValues: string[];

    if (selectAllIsSelected && !selectAllWasSelected) {
      nextSelectedValues = [
        this.selectAllColumnsValue,
        ...availableColumns,
      ];
    } else if (!selectAllIsSelected && selectAllWasSelected) {
      nextSelectedValues = [];
    } else {
      const individuallySelectedColumns = selectedValues.filter(
        (column: string) =>
          column !== this.selectAllColumnsValue
          && availableColumns.indexOf(column) !== -1
      );

      if (
        selectAllIsSelected
        && individuallySelectedColumns.length < availableColumns.length
      ) {
        nextSelectedValues = individuallySelectedColumns;
      } else if (
        availableColumns.length > 0
        && individuallySelectedColumns.length === availableColumns.length
      ) {
        nextSelectedValues = [
          this.selectAllColumnsValue,
          ...availableColumns,
        ];
      } else {
        nextSelectedValues = individuallySelectedColumns;
      }
    }

    this.selectedColumnValues = nextSelectedValues;

    this.updateDisplayedColumns();
    this.saveColumnSelection();
  }
  private updateDisplayedColumns(): void {
    const selectedColumns = this.selectedColumnValues.filter(
      (column: string) => column !== this.selectAllColumnsValue
    );

    this.displayedColumns = this.allColumns.filter((column: string) => {
      if (this.excludeColumns.indexOf(column) !== -1) {
        return false;
      }

      if (column === 'actions') {
        return true;
      }

      return selectedColumns.indexOf(column) !== -1;
    });
  }

  private initializeColumnSelection(): void {
    const availableColumns = this.availableColumnDefinitions.map(
      (column: { key: string; label: string }) => column.key
    );

    const selectedColumns =
      this.tableViewStorageService.getColumns(
        this.tableStorageName,
        availableColumns
      );

    this.selectedColumnValues = [
      ...selectedColumns
    ];

    if (
      availableColumns.length > 0
      && selectedColumns.length === availableColumns.length
    ) {
      this.selectedColumnValues = [
        this.selectAllColumnsValue,
        ...selectedColumns
      ];
    }

    this.updateDisplayedColumns();
  }

  private saveColumnSelection(): void {
    const selectedColumns = this.selectedColumnValues.filter(
      (column: string) =>
        column !== this.selectAllColumnsValue
    );

    this.tableViewStorageService.setColumns(
      this.tableStorageName,
      selectedColumns
    );
  }

}
